import { ErrorMessage } from '@/components/loading-error';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading';
import { PracticeModeButton } from '@/components/cx/PracticeModeButton';
import { CXScenarioConfig } from '@/config/cx-scenarios';
import { getTranslations } from '@/config/cx-translations';
import { useCoachStore } from '@/store/coach';
import { useVoiceEngine } from '@/hooks/useVoiceEngine';
import { useConversation, type Language } from '@elevenlabs/react';
import api from '@/utils/ky';
import { Dialog, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, MessageSquare, Mic, Send } from 'lucide-react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  InlineRealtimeFeedback,
  InlineFeedbackTip,
} from '@/routes/custom/components/InlineRealtimeFeedback';

interface CXRoleplayBaseProps {
  scenario: CXScenarioConfig;
  scenarioCode: string; // e.g., 'grab', 'miso', 'ntuc'
  apiBasePath: string; // e.g., 'api/cx/scenarios/grab'
  assessmentPath: string; // e.g., '/cx/grab/roleplay/{sessionId}/assessment'
  engine?: 'elevenlabs' | 'livekit' | null; // Voice engine to use
}

export function CXRoleplayBase({
  scenario,
  scenarioCode,
  apiBasePath,
  assessmentPath,
  engine,
}: CXRoleplayBaseProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const coachStore = useCoachStore();

  // State
  const [loadingState, setLoadingState] = useState<
    'initial' | 'loading' | 'ready' | 'active' | 'ending' | 'error'
  >('initial');
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(
    sessionId,
  );
  const [isPracticeStarted, setIsPracticeStarted] = useState(false);
  const [mode, setMode] = useState<'voice' | 'chat'>('voice');
  const [textInput, setTextInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Feedback stuff
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [showBriefConversationModal, setShowBriefConversationModal] =
    useState(false);
  const [feedbackTips, setFeedbackTips] = useState<
    Array<{
      type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
      message: string;
      timestamp: Date;
      stage?: string;
      messageIndex: number;
    }>
  >([]);

  // Expandable sections
  const [expandedSections, setExpandedSections] = useState({
    customerDetails: true,
    policyInfo: true,
    greeting: true,
    problemUnderstanding: false,
    empathyTone: false,
    resolutionRecovery: false,
  });

  // Get translations based on ?lang= param or scenario language
  const langOverride = searchParams.get('lang');
  const t = getTranslations(langOverride || scenario.language);

  // Use voice engine hook with language and preferred engine
  const voiceEngine = useVoiceEngine(scenario.language, engine || undefined);
  const isAISpeaking = voiceEngine.isSpeaking;

  // Text-only conversation for chat mode
  const textConversation = useConversation({
    textOnly: true,
    onConnect: () => {
      console.log('Connected to text conversation');
      setLoadingState('active');
    },
    onMessage: ({ source, message }) => {
      if (source === 'ai' && message) {
        setTranscript((prev) => [
          ...prev,
          {
            source: 'ai',
            message,
            timestamp: new Date(),
          },
        ]);
        setIsTyping(false);
      }
    },
    onError: (error) => {
      console.error('Text conversation error:', error);
      setIsTyping(false);
      toast.error('Failed to send message');
    },
  });

  // Refs
  const startTimeRef = useRef<number>(0);
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);

  // Refs for auto-scrolling to latest message
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, isTyping, isAISpeaking]);

  const initializeScenario = useCallback(async () => {
    try {
      setLoadingState('loading');
      // Scenario is passed as prop, so just set ready
      setLoadingState('ready');
    } catch (error) {
      console.error('Error initializing scenario:', error);
      setError('Failed to load scenario. Please try again.');
      setLoadingState('error');
    }
  }, []);

  useEffect(() => {
    initializeScenario();
  }, [initializeScenario]);

  const handleStartPractice = async (selectedMode: 'voice' | 'chat') => {
    // Prevent double-clicking
    if (loadingState !== 'ready') {
      console.log('⚠️ Already starting practice or not ready, ignoring duplicate click');
      return;
    }

    // Set the mode based on the button clicked and immediately start practice UI
    setMode(selectedMode);
    setIsPracticeStarted(true);
    setLoadingState('loading');
    try {
      // Create session with language parameter
      const sessionData = await api
        .post(`${apiBasePath}/sessions`, {
          json: {
            userId: coachStore.userId,
            language: scenario.language, // Pass the scenario's language
          },
        })
        .json<{ sessionId: string; aiConfig: any }>();

      setCurrentSessionId(sessionData.sessionId);

      // For chat mode, use ElevenLabs text-only mode
      if (selectedMode === 'chat') {
        console.log('Starting text-only chat session...');

        try {
          // For text mode, always use ElevenLabs regardless of engine
          // (LiveKit is only used for voice mode)

          // Prepare agent config with optional language
          const agentConfig: any = {
            prompt: {
              prompt:
                scenario.aiConfig.systemPrompt +
                '\n\nTEXT CHAT MODE ADJUSTMENTS:\n' +
                '- You are chatting via text, not on a phone call\n' +
                '- Write naturally complete messages, not fragments\n' +
                '- After greeting confirmation, provide a brief introduction with your concern\n' +
                '- Example good response: "Yes, this is Miso. My name is Kim Myung-ok. I had a cleaning service yesterday and I need to report some problems."\n' +
                '- Example bad response: "Yes." [send] "This is Miso." [send] "I have problem." [send]\n' +
                '- Still maintain the gradual information reveal, but in complete thoughts\n' +
                '- Do not use [sigh], [pause] or emotion indicators - express emotions through words',
            },
            firstMessage: scenario.aiConfig.firstMessage,
          };

          // Only add language if it's supported by ElevenLabs Language type
          // Thai (th) is supported for text mode but may not be in the Language type
          const supportedLanguages = ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'hi', 'ar'];
          if (supportedLanguages.includes(scenario.language)) {
            agentConfig.language = scenario.language as Language;
          }

          const textSessionId = await textConversation.startSession({
            agentId: sessionData.aiConfig.agentId,
            connectionType: 'websocket',
            overrides: {
              agent: agentConfig,
              ...(scenario.aiConfig.voiceId
                ? { tts: { voiceId: scenario.aiConfig.voiceId } }
                : {}),
            },
          });

          console.log(
            'Text conversation initialized with session ID:',
            textSessionId,
          );
          // Loading state will be set to 'active' by onConnect callback
          startTimeRef.current = Date.now();

          // Send contextual update for chat mode
          if (textConversation.sendContextualUpdate) {
            textConversation.sendContextualUpdate(
              'IMPORTANT: User is interacting via TEXT CHAT, not voice. Adjust your responses accordingly:\n' +
                '- Write complete, natural responses - do not split single thoughts across multiple messages\n' +
                '- After greeting and confirmation, you can provide a fuller response about your issue\n' +
                '- Example: "Yes, this is Miso. My name is Kim Myung-ok. I had a cleaning service yesterday and there are some serious issues I need to discuss."\n' +
                '- Still wait for agent to ask for specific details before providing order numbers or full complaint details\n' +
                "- In text chat, it's natural to write 2-3 sentences at once, not just fragments",
            );
          }
        } catch (error) {
          console.error('Failed to start text conversation:', error);
          throw error;
        }

        return;
      }

      // Voice mode: Request microphone access first
      await navigator.mediaDevices.getUserMedia({ audio: true });

      console.log('Session data:', sessionData);

      // Configure AI agent for customer service scenario (voice mode only)
      const voiceConfig = {
        agentId: sessionData.aiConfig.agentId,
        prompt: scenario.aiConfig.systemPrompt,
        firstMessage: scenario.aiConfig.firstMessage,
        language: scenario.aiConfig.language,
        voiceId: scenario.aiConfig.voiceId,
        // For LiveKit - use new room name format
        roomName: `cx-${scenarioCode}-${sessionData.sessionId}`,
        participantName: coachStore.userId || 'agent',
        // Metadata for LiveKit
        metadata: {
          language: scenario.language,
          sessionType: 'cx',
          scenarioCode: scenarioCode,
        },
      };

      const conversationId = await voiceEngine.start(voiceConfig, {
        onConnect: () => {
          console.log('Connected to conversation');
          startTimeRef.current = Date.now();
          setLoadingState('active');
        },
        onError: (error) => {
          console.error('Conversation error:', error);
          setError(error?.message || 'Connection Error');
        },
        onMessage: ({ source, message }) => {
          console.log('Message:', source, message);
          setTranscript((prev) => [
            ...prev,
            {
              source,
              message,
              timestamp: new Date(),
            },
          ]);
        },
        onDebug: (data) => {
          console.log('Debug:', data);
        },
      });
      console.log('Conversation started:', conversationId);
    } catch (error) {
      console.error('Error starting practice:', error);
      setError('Failed to start practice session');
      setLoadingState('error');
      setIsPracticeStarted(false);
    }
  };

  const handleEndPractice = async () => {
    try {
      setLoadingState('ending');
      setIsLoadingFeedback(true);

      if (mode === 'voice') {
        await voiceEngine.stop();
      } else if (mode === 'chat' && textConversation.status === 'connected') {
        await textConversation.endSession();
      }

      const formattedTranscript = transcript
        .map(
          (msg) =>
            `${msg.source === 'ai' ? 'Customer' : 'Agent'}: ${msg.message}`,
        )
        .join('\n');

      const feedback = await api
        .post(`${apiBasePath}/sessions/${currentSessionId}/assessment`, {
          json: {
            transcript: formattedTranscript,
            duration: (Date.now() - startTimeRef.current) / 1000,
            scenarioId: scenario.id,
          },
          timeout: 90000,
        })
        .json();

      console.log('assessment posted, result:', feedback);

      if ((feedback as any).tooBrief) {
        setShowBriefConversationModal(true);
        setIsLoadingFeedback(false);
        return;
      }

      // Navigate to assessment page
      navigate(assessmentPath.replace('{sessionId}', currentSessionId!));
    } catch (error) {
      console.error('Error ending practice:', error);
      toast.error('Failed to generate feedback. Please try again.');
    } finally {
      setIsLoadingFeedback(false);
      setLoadingState('ready');
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextInput(e.target.value);

    // Send user activity to prevent agent interruption while typing
    if (
      mode === 'chat' &&
      textConversation.status === 'connected' &&
      textConversation.sendUserActivity
    ) {
      textConversation.sendUserActivity();
    }
  };

  const handleTextInputSubmit = async () => {
    if (!textInput.trim()) return;

    const userMessage = textInput.trim();
    setTextInput(''); // Clear input immediately
    setIsTyping(true);

    try {
      if (mode === 'chat') {
        // Check if connected to text conversation
        if (textConversation.status !== 'connected') {
          console.error('Not connected to text conversation');
          toast.error('Please wait for the connection to establish');
          setIsTyping(false);
          return;
        }

        // Add user message to transcript
        setTranscript((prev) => [
          ...prev,
          {
            source: 'user',
            message: userMessage,
            timestamp: new Date(),
          },
        ]);

        // Send message through ElevenLabs text conversation
        console.log('Sending user message:', userMessage);
        textConversation.sendUserMessage(userMessage);
        console.log('Message sent successfully');
      } else {
        // Voice mode doesn't support text input currently
        toast.error('Text input is not supported in voice mode');
        setIsTyping(false);
      }
    } catch (error) {
      console.error('Error sending text message:', error);
      toast.error('Failed to send message');
      setIsTyping(false);
    }
  };

  if (loadingState === 'error' && error) {
    return <ErrorMessage message={error} onRetry={initializeScenario} />;
  }

  return (
    <div className="h-screen bg-gray-50 overflow-hidden flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <img
                src="/logos/Hupo_Icon_Orange.svg"
                alt="Logo"
                className="size-7"
              />
            </div>
            <div className="flex items-center space-x-2">
              <h1 className="text-lg font-semibold text-gray-900">
                {t.roleplayPractice}
              </h1>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t.serviceRecovery}
              </span>
            </div>
          </div>
          {isPracticeStarted && (
            <button
              onClick={handleEndPractice}
              className="inline-flex items-center px-4 py-2 text-sm text-white hover:text-white border border-[#FF4B0A] bg-[#FF4B0A] rounded-full shadow-sm hover:bg-[#E64409] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF4B0A]"
            >
              {t.endSession}
            </button>
          )}
        </div>
      </header>

      <AnimatePresence mode="wait">
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel - Practice Details */}
          <div className="w-80 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col h-full">
            <div className="overflow-y-auto flex-1">
              <div className="p-6">
                <div className="mb-6">
                  <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                    {t.practiceDetails}
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {scenario.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {scenario.description}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {t.practiceObjectives}
                  </h4>
                  <ul className="space-y-2">
                    {scenario.practiceObjectives?.map(
                      (objective: string, index: number) => (
                        <li
                          key={index}
                          className="flex items-start text-sm text-gray-600"
                        >
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          {objective}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    {t.mainIssue}
                  </h4>
                  <p className="text-sm text-gray-600 italic">
                    {scenario.mainIssue}
                  </p>
                </div>
                <hr className="my-4 border-gray-200" />

                {/* Service Delivery Sections */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                    {t.serviceDelivery}
                  </h4>

                  {/* Greeting & Identity Verification */}
                  <div className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('greeting')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-700">
                        {scenario.serviceDelivery.greetingIdentity.title}
                      </span>
                      {expandedSections.greeting ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    {expandedSections.greeting && (
                      <div className="p-3 pt-0 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          {scenario.serviceDelivery.greetingIdentity.content.map(
                            (item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {item}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Problem Understanding */}
                  <div className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('problemUnderstanding')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-700">
                        {scenario.serviceDelivery.problemUnderstanding.title}
                      </span>
                      {expandedSections.problemUnderstanding ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    {expandedSections.problemUnderstanding && (
                      <div className="p-3 pt-0 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          {scenario.serviceDelivery.problemUnderstanding.content.map(
                            (item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {item}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Empathy & Tone */}
                  <div className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('empathyTone')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-700">
                        {scenario.serviceDelivery.empathyTone.title}
                      </span>
                      {expandedSections.empathyTone ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    {expandedSections.empathyTone && (
                      <div className="p-3 pt-0 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          {scenario.serviceDelivery.empathyTone.content.map(
                            (item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {item}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Resolution & Recovery */}
                  <div className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('resolutionRecovery')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-700">
                        {scenario.serviceDelivery.resolutionRecovery.title}
                      </span>
                      {expandedSections.resolutionRecovery ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    {expandedSections.resolutionRecovery && (
                      <div className="p-3 pt-0 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          {scenario.serviceDelivery.resolutionRecovery.content.map(
                            (item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {item}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Center Panel - Customer Avatar and Practice */}
          <div className="flex-1 flex flex-col h-full min-h-0 min-w-0">
            {mode === 'voice' ? (
              // Voice Mode - Avatar Interface
              <div
                className={`flex-1 flex bg-gray-100 min-h-0 ${
                  isPracticeStarted
                    ? 'flex-col'
                    : 'items-center justify-center'
                }`}
              >
                {loadingState === 'loading' ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t.preparingVoiceSession}
                    </h3>
                    <p className="text-gray-500">
                      {t.connectingToCustomer.replace(
                        '{name}',
                        scenario.persona.name,
                      )}
                    </p>
                  </div>
                ) : isLoadingFeedback ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t.analyzingPerformance}
                    </h3>
                    <p className="text-gray-500">{t.generatingFeedback}</p>
                  </div>
                ) : isPracticeStarted ? (
                  <div className="flex flex-col h-full">
                    {/* Compact persona header */}
                    <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-gray-200 flex-shrink-0">
                      <div className="relative">
                        <img
                          alt={scenario.persona.name}
                          src={scenario.persona.avatarUrl}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        {isAISpeaking && (
                          <div className="absolute inset-0 border-2 border-blue-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-sm font-semibold text-gray-900 truncate">
                          {scenario.persona.name}
                        </h2>
                        <p className="text-xs text-gray-500 truncate">
                          {scenario.persona.role}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 flex-shrink-0">
                        {isAISpeaking
                          ? t.customerIsSpeaking
                          : t.customerIsListening}
                      </span>
                    </div>

                    {/* Transcript - fills remaining space */}
                    <div
                      className="flex-1 overflow-y-auto bg-gray-50"
                      style={{ height: 0 }}
                    >
                      <div className="p-4 space-y-3">
                        {transcript.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">
                            Conversation will appear here...
                          </p>
                        ) : (
                          transcript.map((msg, index) => (
                            <Fragment key={index}>
                              <div
                                className={`flex ${
                                  msg.source === 'ai'
                                    ? 'justify-start'
                                    : 'justify-end'
                                }`}
                              >
                                <div
                                  className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                                    msg.source === 'ai'
                                      ? 'bg-white text-gray-800 border border-gray-200 shadow-sm'
                                      : 'bg-blue-600 text-white shadow-sm'
                                  }`}
                                >
                                  {msg.source === 'ai' && (
                                    <div className="flex items-center mb-1">
                                      <Bot className="h-3 w-3 mr-1 text-gray-500" />
                                      <span className="font-medium text-gray-700 text-xs">
                                        {scenario.persona.name}
                                      </span>
                                    </div>
                                  )}
                                  <p className="leading-relaxed">
                                    {msg.message}
                                  </p>
                                  <div className="mt-1 text-xs opacity-70">
                                    {msg.timestamp.toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </div>
                                </div>
                              </div>
                              {msg.source === 'user' &&
                                feedbackTips
                                  .filter((tip) => tip.messageIndex === index)
                                  .map((tip, tipIdx) => (
                                    <InlineFeedbackTip
                                      key={`tip-${index}-${tipIdx}`}
                                      tip={tip}
                                      delay={tipIdx * 0.1}
                                    />
                                  ))}
                            </Fragment>
                          ))
                        )}
                        {(isTyping || isAISpeaking) && (
                          <div className="flex justify-start">
                            <div className="bg-white rounded-lg px-3 py-2 border border-gray-200 shadow-sm">
                              <div className="flex items-center">
                                <Bot className="h-3 w-3 mr-1 text-gray-500" />
                                <span className="font-medium text-gray-700 text-xs mr-2">
                                  {scenario.persona.name}
                                </span>
                                <div className="flex space-x-1">
                                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                                  <div
                                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.1s' }}
                                  ></div>
                                  <div
                                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.2s' }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Auto-scroll target for transcript */}
                        <div ref={transcriptEndRef} />
                      </div>
                    </div>

                    {/* Realtime feedback analyzer (renders nothing, just makes API calls) */}
                    <InlineRealtimeFeedback
                      transcript={transcript}
                      sessionId={currentSessionId}
                      hasCompetitorData={false}
                      setFeedbackTips={setFeedbackTips}
                      apiBasePath={apiBasePath}
                      transcriptRoleLabels={{ ai: 'Customer', user: 'Advisor' }}
                    />
                  </div>
                ) : (
                  <div className="text-center max-w-md">
                    <div className="mb-8">
                      <img
                        alt={scenario.persona.name}
                        src={scenario.persona.avatarUrl}
                        className="w-48 h-48 rounded-full object-cover mx-auto mb-4"
                      />
                    </div>

                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {scenario.persona.name}
                    </h2>
                    <p className="text-gray-600 mb-8">
                      {scenario.persona.role}
                    </p>

                    <div className="space-y-4">
                      <p className="text-sm text-gray-500 text-center">
                        {t.choosePracticeMode}
                      </p>
                      <div className="flex gap-3">
                        <PracticeModeButton
                          icon={Mic}
                          title={t.voicePractice}
                          subtitle={t.realCallSimulation}
                          onClick={() => handleStartPractice('voice')}
                          disabled={loadingState !== 'ready'}
                        />
                        {!scenario.disableChatMode && (
                          <PracticeModeButton
                            icon={MessageSquare}
                            title={t.chatPractice}
                            subtitle={t.textBasedPractice}
                            onClick={() => handleStartPractice('chat')}
                            disabled={loadingState !== 'ready'}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Chat Mode - Chat Interface
              <div className="h-full flex flex-col bg-gray-50">
                {loadingState === 'loading' ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t.preparingChatSession}
                      </h3>
                      <p className="text-gray-500">
                        {t.settingUpConversation.replace(
                          '{name}',
                          scenario.persona.name,
                        )}
                      </p>
                    </div>
                  </div>
                ) : isLoadingFeedback ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t.analyzingPerformance}
                      </h3>
                      <p className="text-gray-500">{t.generatingFeedback}</p>
                    </div>
                  </div>
                ) : isPracticeStarted ? (
                  <div className="h-full flex flex-col">
                    {/* Chat Messages - Fixed height with independent scrolling */}
                    <div
                      className="flex-1 overflow-y-auto bg-gray-50"
                      style={{ height: 0 }}
                    >
                      <div className="p-6 space-y-4">
                        <AnimatePresence>
                          {transcript.map((msg, index) => (
                            <Fragment key={index}>
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  type: 'spring',
                                  stiffness: 50,
                                  damping: 20,
                                }}
                                className={`flex ${
                                  msg.source === 'ai'
                                    ? 'justify-start'
                                    : 'justify-end'
                                }`}
                              >
                                <div
                                  className={`max-w-[75%] rounded-lg p-4 shadow-sm ${
                                    msg.source === 'ai'
                                      ? 'bg-white text-gray-900'
                                      : 'bg-blue-600 text-white'
                                  }`}
                                >
                                  {msg.source === 'ai' && (
                                    <div className="flex items-center mb-2">
                                      <Bot className="h-4 w-4 mr-2 text-gray-500" />
                                      <span className="text-sm font-medium text-gray-700">
                                        {scenario.persona.name}
                                      </span>
                                    </div>
                                  )}
                                  <p className="text-sm">{msg.message}</p>
                                  <span className="text-xs opacity-70 mt-1 block">
                                    {msg.timestamp.toLocaleTimeString()}
                                  </span>
                                </div>
                              </motion.div>
                              {msg.source === 'user' &&
                                feedbackTips
                                  .filter((tip) => tip.messageIndex === index)
                                  .map((tip, tipIdx) => (
                                    <InlineFeedbackTip
                                      key={`tip-${index}-${tipIdx}`}
                                      tip={tip}
                                      delay={tipIdx * 0.1}
                                    />
                                  ))}
                            </Fragment>
                          ))}
                          {isTyping && (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex justify-start"
                            >
                              <div className="max-w-[75%] rounded-lg p-4 shadow-sm bg-white text-gray-900">
                                <div className="flex items-center mb-2">
                                  <Bot className="h-4 w-4 mr-2 text-gray-500" />
                                  <span className="text-sm font-medium text-gray-700">
                                    {scenario.persona.name}
                                  </span>
                                </div>
                                <div className="flex space-x-1">
                                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                  <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.1s' }}
                                  ></div>
                                  <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.2s' }}
                                  ></div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Auto-scroll target */}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Realtime feedback analyzer for chat mode */}
                    <InlineRealtimeFeedback
                      transcript={transcript}
                      sessionId={currentSessionId}
                      hasCompetitorData={false}
                      setFeedbackTips={setFeedbackTips}
                      apiBasePath={apiBasePath}
                      transcriptRoleLabels={{ ai: 'Customer', user: 'Advisor' }}
                    />

                    {/* Chat Input - Fixed at bottom */}
                    <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
                      <div className="flex items-center space-x-4">
                        <div className="flex-1 relative">
                          <textarea
                            value={textInput}
                            onChange={handleTextInputChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (textInput.trim()) {
                                  handleTextInputSubmit();
                                }
                              }
                            }}
                            placeholder={t.typeYourResponse}
                            className="w-full p-3 pr-12 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={1}
                            style={{ minHeight: '44px' }}
                          />
                          <Button
                            onClick={handleTextInputSubmit}
                            disabled={!textInput.trim()}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            variant="ghost"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md">
                      <div className="mb-8">
                        <img
                          alt={scenario.persona.name}
                          src={scenario.persona.avatarUrl}
                          className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
                        />
                      </div>

                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {scenario.persona.name}
                      </h2>
                      <p className="text-gray-600 mb-8">
                        {scenario.persona.role}
                      </p>

                      <div className="space-y-4">
                        <p className="text-sm text-gray-500 text-center">
                          {t.choosePracticeMode}
                        </p>
                        <div className="flex gap-3">
                          <PracticeModeButton
                            icon={Mic}
                            title={t.voicePractice}
                            subtitle={t.realCallSimulation}
                            onClick={() => handleStartPractice('voice')}
                            disabled={loadingState !== 'ready'}
                          />
                          {!scenario.disableChatMode && (
                            <PracticeModeButton
                              icon={MessageSquare}
                              title={t.chatPractice}
                              subtitle={t.textBasedPractice}
                              onClick={() => handleStartPractice('chat')}
                              disabled={loadingState !== 'ready'}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Customer Details */}
          <div className="w-80 bg-white border-l border-gray-200 flex-shrink-0 flex flex-col h-full">
            <div className="overflow-y-auto flex-1">
              <div className="p-6">
                {/* Customer Details */}
                <div className="mb-6">
                  <button
                    onClick={() => toggleSection('customerDetails')}
                    className="w-full flex items-center justify-between mb-3"
                  >
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                      {t.customerDetails}
                    </h3>
                    {expandedSections.customerDetails ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                    )}
                  </button>

                  {expandedSections.customerDetails && (
                    <div className="space-y-4">
                      {/* Order Details (for Grab) */}
                      {scenario.orderDetails && (
                        <>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {t.orderId}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {scenario.orderDetails.orderId}
                            </p>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {scenario.orderDetails.sectionLabel || t.items}
                            </h4>
                            <ul className="space-y-2">
                              {scenario.orderDetails.items?.map(
                                (item: any, index: number) => (
                                  <li
                                    key={index}
                                    className="flex justify-between text-sm text-gray-600 gap-4"
                                  >
                                    <span className="text-gray-500 flex-shrink-0">
                                      {item.name}
                                    </span>
                                    <span className="text-right font-medium text-gray-900">
                                      {item.price}
                                    </span>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>

                          {scenario.orderDetails.promisedETA && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                                {t.promisedVsActual}
                              </h4>
                              <p className="text-sm text-gray-600 mb-4">
                                {scenario.orderDetails.promisedETA} →{' '}
                                {scenario.orderDetails.actualDropOff}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Service Details (for Miso) */}
                      {scenario.serviceDetails && (
                        <>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {t.serviceType}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {scenario.serviceDetails.serviceType}
                            </p>
                          </div>

                          {scenario.serviceDetails.scheduledTime && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                                {t.scheduledVsActual}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {scenario.serviceDetails.scheduledTime} →{' '}
                                {scenario.serviceDetails.duration}
                              </p>
                            </div>
                          )}

                          {scenario.serviceDetails.issue && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                                {t.issue}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {scenario.serviceDetails.issue}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">
                          {t.location}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          {scenario.customerInfo.location}
                        </p>
                      </div>

                      {(scenario.accountDetails ||
                        scenario.customerInfo.signupDate ||
                        scenario.customerInfo.membershipLevel ||
                        scenario.customerInfo.loyaltyStatus ||
                        scenario.customerInfo.bookingReference) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            {t.accountInformation}
                          </h4>
                          <ul className="text-sm text-gray-600 space-y-2 mb-4">
                            {scenario.accountDetails && (
                              <>
                                <li className="flex items-center">
                                  <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                  Account Number:{' '}
                                  {scenario.accountDetails.accountNumber}
                                </li>
                                {scenario.accountDetails.address && (
                                  <li className="flex items-center">
                                    <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                    Address: {scenario.accountDetails.address}
                                  </li>
                                )}
                                {scenario.accountDetails.postalCode && (
                                  <li className="flex items-center">
                                    <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                    Postal Code:{' '}
                                    {scenario.accountDetails.postalCode}
                                  </li>
                                )}
                                {scenario.accountDetails.contactNumber && (
                                  <li className="flex items-center">
                                    <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                    Contact Number:{' '}
                                    {scenario.accountDetails.contactNumber}
                                  </li>
                                )}
                                <li className="flex items-center">
                                  <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                  Plan: {scenario.accountDetails.plan}
                                  {scenario.accountDetails.monthlyRate
                                    ? ` (${scenario.accountDetails.monthlyRate})`
                                    : ''}
                                </li>
                              </>
                            )}
                            {scenario.customerInfo.signupDate && (
                              <li className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                {t.signedUpOn} {scenario.customerInfo.signupDate}
                              </li>
                            )}
                            {scenario.customerInfo.membershipLevel && (
                              <li className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                {scenario.customerInfo.membershipLevel}
                              </li>
                            )}
                            {scenario.customerInfo.loyaltyStatus && (
                              <li className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                {t.loyaltyStatus}:{' '}
                                {scenario.customerInfo.loyaltyStatus}
                              </li>
                            )}
                            {scenario.customerInfo.bookingReference && (
                              <li className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                {t.bookingReference}:{' '}
                                {scenario.customerInfo.bookingReference}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {scenario.customerInfo.recentIncidents &&
                        scenario.customerInfo.recentIncidents.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {t.recentIncidents}
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-2">
                              {scenario.customerInfo.recentIncidents.map(
                                (incident, idx) => (
                                  <li key={idx} className="flex items-center">
                                    <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                                    {incident}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  )}
                </div>
                <hr className="my-4 border-gray-200" />

                {/* Policy Information */}
                <div>
                  <button
                    onClick={() => toggleSection('policyInfo')}
                    className="w-full flex items-center justify-between mb-3"
                  >
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                      {t.policyInformation}
                    </h3>
                    {expandedSections.policyInfo ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                    )}
                  </button>

                  {expandedSections.policyInfo && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          {scenario.companyName} {t.policies}
                        </h4>

                        <div className="space-y-3">
                          {Object.entries(scenario.policies).map(
                            ([key, policy]) => (
                              <div key={key}>
                                <h5 className="text-sm font-semibold text-gray-700">
                                  {(t as any)[key] ||
                                    key
                                      .replace(/([A-Z])/g, ' $1')
                                      .replace(/^./, (str) => str.toUpperCase())}
                                </h5>
                                <div className="mt-1 text-xs text-gray-600">
                                  <div className="mb-1">
                                    <strong>{t.trigger}:</strong>{' '}
                                    {policy.trigger}
                                  </div>
                                  <div>
                                    <strong>{t.remedy}:</strong> {policy.remedy}
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatePresence>

      {/* Too Brief Conversation Modal */}
      <Transition appear show={showBriefConversationModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowBriefConversationModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-center mb-4">
                    <div className="rounded-full bg-amber-100 p-3">
                      <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 text-center"
                  >
                    {t.conversationTooBrief}
                  </Dialog.Title>
                  <div className="mt-3">
                    <p className="text-sm text-gray-500 text-center">
                      {t.conversationTooBriefMessage}
                    </p>
                  </div>

                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-full border border-transparent bg-orange-500 px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                      onClick={handleRetry}
                    >
                      {t.tryAgain}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && loadingState !== 'active' && (
          <LoadingOverlay
            state={loadingState}
            title={
              loadingState === 'loading'
                ? t.preparingVoiceSession
                : loadingState === 'ending'
                  ? t.analyzingPerformance
                  : undefined
            }
            message={
              loadingState === 'loading'
                ? t.connectingToCustomer.replace('{name}', scenario.persona.name)
                : loadingState === 'ending'
                  ? t.generatingFeedback
                  : undefined
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}
